import { InsightTextField } from '../contributions/insight-base.model';
import { SupportedLocale } from '../language/locale.model';

/**
 * Types for `POST api/v1/insights/{id}/send-for-translation` — the editorial
 * "translate this insight" action.
 *
 * **There is no strategy to choose.** Earlier versions of this endpoint took `modes` and
 * `commit_mode`, because it existed to compare how plain text / HTML / Markdown survived
 * the provider. That comparison is over: Markdown travels as HTML and comes back as
 * Markdown, and how a field travels is now derived on the backend from the format the
 * model declares for that field. `content_format` on a result reports which one applied.
 */

/** What the backend's model declares the field to hold, which is how it travelled. */
export type ContentFormat = 'plain' | 'markdown';

export interface SendInsightForTranslationRequest {
  /** Required, at least one. Must not contain `source_lang`. */
  target_langs: SupportedLocale[];
  /** Defaults to every field that has content in the source locale. */
  fields?: InsightTextField[];
  /** Defaults to 'en'. */
  source_lang?: SupportedLocale;
  /** Domain hint passed to the provider; not itself translated. Max 500 chars. */
  context?: string;
  /**
   * Translate and report, but write nothing. The backend defaults this to `false`.
   *
   * Note it is not a free preview: the provider is still called and still bills, so
   * previewing and then saving costs twice.
   */
  dry_run?: boolean;
}

interface TranslationResultBase {
  field: InsightTextField;
  source_locale: SupportedLocale;
  target_lang: SupportedLocale;
  content_format: ContentFormat;
  /** True for a result that was written back to the insight. */
  committed: boolean;
  source_chars: number;
}

export interface TranslationResultOk extends TranslationResultBase {
  /** Byte-identical to what a save writes — the backend sanitises before returning. */
  text: string;
  /** Provider-billed characters — markup is billed, so a Markdown field costs more. */
  billed_characters: number | null;
  duration_ms: number | null;
  model_type_used: string | null;
  error?: never;
}

export interface TranslationResultFailed extends TranslationResultBase {
  committed: false;
  error: string;
  text?: never;
}

export type TranslationRunResult = TranslationResultOk | TranslationResultFailed;

export interface SendInsightForTranslationResponse {
  insight_id: number;
  slug: string;
  source_locale: SupportedLocale;
  dry_run: boolean;
  /** True if anything was written to the insight. */
  committed: boolean;
  results: TranslationRunResult[];
  /** Present on 422 (nothing to translate) and 502 (everything failed). */
  message?: string;
}

/**
 * Results are a discriminated union, and a `200` does not mean everything worked — a
 * single failing field × language is reported inside `results` rather than failing the
 * request. Narrow with this before reading `text`.
 */
export function isFailedTranslationResult(result: TranslationRunResult): result is TranslationResultFailed {
  return 'error' in result;
}
